import { Alert, Badge, LoadingState } from '../../../shared/styles/components/feedback.styles';
import {
   AuditTrailEntry,
   FactorMetadata,
   YearlyEmissionValues
} from '../types/emission-factor.api';
import { Caption, EmptyState, EmptyStateText, FooterSpacer } from '../../../shared/styles/components/primitives.styles';
import {
   ChartControlGroup,
   ChartControls,
   ClearButton,
   DetailsPanelCloseWrapper,
   DetailsPanelLabel,
   DismissibleAlertRow,
   DropdownGrid,
   DropdownWithClear,
   FeedstockToggle,
   FooterValidationMessage,
   HistoryNavigation,
   ModalTitleRow,
   PositionedDetailsContainer,
   Tab,
   TabContainer,
   WideModalContainer,
   WideModalContent,
} from './EmissionFactorModal.styles';
import ComboboxDropdown, { ComboboxOption } from '../../../shared/components/ComboboxDropdown';
import { FEEDSTOCKS, FEEDSTOCK_COLORS, FEEDSTOCK_LABELS, FeedstockKey, MetricKey } from '../types/emission-factor.constants';
import Loading, { LoadingBall } from '@ingka/loading';
import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ReviewChangesModal, computeChanges } from './ReviewChangesModal';
import { fromPercent, toPercent, validateYearlyShares } from '../utils/emission-factor.utils';

import Button from '@ingka/button';
import { DataTable } from '../../../shared/styles/components/data-table.styles';
import { DetailsPanel } from '../../../shared/styles/components/details-panel.styles';
import EmissionFactorCharts from './EmissionFactorCharts';
import { IconButton } from '../../../shared/components/IconButton';
import { Modal } from '../../../shared/styles/components/modal.styles';
import { NumberInput } from '../../../shared/styles/components/forms.styles';
import ReactDOM from 'react-dom';
import SSRIcon from '@ingka/ssr-icon';
import { IconButton as SharedIconButton } from '../../../shared/styles/components/forms.styles';
import Status from '@ingka/status';
import chevronLeftIcon from '@ingka/ssr-icon/paths/chevron-left';
import chevronRightIcon from '@ingka/ssr-icon/paths/chevron-right';
import crossIcon from '@ingka/ssr-icon/paths/cross';
import { formatDate } from '../../../shared/utils/formatters.utils';
import { formatNumber } from '../../../shared/utils/formatters.utils';
import { ProgressStatusColor } from '../../../shared/utils/progress.utils';
import { theme } from '../../../shared/styles/core/theme';
import { useEmissionFactorModal } from '../hooks/useEmissionFactorModal';
import { useEscapeKey } from '../../../shared/utils/keyboard.utils';
import warningTriangleIcon from '@ingka/ssr-icon/paths/warning-triangle';

type ModalTab = 'data' | 'charts';

interface SelectedCellInfo {
   year: number;
   feedstock: FeedstockKey;
}

interface HierarchyOption {
   id: string;
   displayName: string;
   progressText?: string;
   progressStatusColor?: ProgressStatusColor;
   hasData?: boolean;
}

const VISIBLE_METRICS: Set<MetricKey> = new Set(['share', 'emission']);

const renderHierarchyMeta = ({ progressStatusColor, progressText }: HierarchyOption) => {
   if (!progressStatusColor && !progressText) return undefined;
   return (
      <>
         {progressStatusColor && <Status statusColor={progressStatusColor} />}
         {progressText && <span>{progressText}</span>}
      </>
   );
};

const toComboboxOptions = <T extends HierarchyOption>(
   items: T[],
   hasDataFn?: (item: T) => boolean
): ComboboxOption[] =>
   items.map((item) => ({
      id: item.id,
      displayName: item.displayName,
      meta: renderHierarchyMeta(item),
      hasData: hasDataFn ? hasDataFn(item) : undefined,
   }));

interface ValueCellProps {
   value: number | null;
   year: number;
   feedstockKey: FeedstockKey;
   field: MetricKey;
   isSelected: boolean;
   disabled: boolean;
   validationError?: string | null;
   onChange: (year: number, feedstock: FeedstockKey, field: MetricKey, value: number | null) => void;
   onSelect: (cell: SelectedCellInfo) => void;
}

const ValueCell: React.FC<ValueCellProps> = memo(
   ({ value, year, feedstockKey, field, isSelected, disabled, validationError, onChange, onSelect }) => {
      const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
         const rawValue = e.target.value ? parseFloat(e.target.value) : null;
         onChange(year, feedstockKey, field, field === 'share' ? fromPercent(rawValue) : rawValue);
      };

      const handleFocus = () => {
         onSelect({ year, feedstock: feedstockKey });
      };

      const rawValue = field === 'share' ? toPercent(value) : value;
      const displayValue = rawValue !== null ? Math.round(rawValue * 100) / 100 : '';

      return (
         <DataTable.Cell $selected={isSelected}>
            <NumberInput
               type="number"
               step="0.01"
               value={displayValue}
               onChange={handleChange}
               onFocus={handleFocus}
               disabled={disabled}
               style={validationError ? { borderColor: theme.colors.state.error } : undefined}
               title={validationError ?? undefined}
            />
         </DataTable.Cell>
      );
   }
);

ValueCell.displayName = 'ValueCell';

interface HistorySectionProps {
   title: string;
   value: number | null;
   auditTrail: AuditTrailEntry[];
   latestBy?: string | null;
   latestAt?: string | null;
   latestComment?: string | null;
   asPercent?: boolean;
}

const HistorySection: React.FC<HistorySectionProps> = memo(({
   title,
   value,
   auditTrail,
   latestBy,
   latestAt,
   latestComment,
   asPercent = false,
}) => {
   const [currentIndex, setCurrentIndex] = useState(0);

   const sortedAuditTrail = useMemo(
      () => [...auditTrail].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
      [auditTrail]
   );

   const hasHistory = sortedAuditTrail.length > 1;
   const totalEntries = Math.max(sortedAuditTrail.length, 1);

   useEffect(() => setCurrentIndex(0), [sortedAuditTrail]);

   const currentEntry = sortedAuditTrail[currentIndex];
   const isAtCurrent = currentIndex === 0;

   const displayBy = currentEntry?.updatedBy ?? (isAtCurrent ? latestBy : null);
   const displayAt = currentEntry?.updatedAt ?? (isAtCurrent ? latestAt : null);
   const displayComment = currentEntry?.comment ?? (isAtCurrent ? latestComment : null);
   const rawValue = currentEntry?.value ?? (isAtCurrent ? value : null);
   const displayValue = asPercent ? toPercent(rawValue) : rawValue;

   return (
      <DetailsPanel.Section>
         <DetailsPanel.SectionHeader>
            <DetailsPanel.SectionTitle>{title}</DetailsPanel.SectionTitle>
         </DetailsPanel.SectionHeader>
         <DetailsPanel.SectionContent>
            <DetailsPanel.Row>
               <DetailsPanel.Label>Value:</DetailsPanel.Label>
               <DetailsPanel.Value>{formatNumber(displayValue)}</DetailsPanel.Value>
            </DetailsPanel.Row>
            <DetailsPanel.Row>
               <DetailsPanel.Label>Updated by:</DetailsPanel.Label>
               <DetailsPanel.Value>{displayBy ?? '-'}</DetailsPanel.Value>
            </DetailsPanel.Row>
            <DetailsPanel.Row>
               <DetailsPanel.Label>Date:</DetailsPanel.Label>
               <DetailsPanel.Value>{formatDate(displayAt, 'datetime') ?? '-'}</DetailsPanel.Value>
            </DetailsPanel.Row>
            <DetailsPanel.Row>
               <DetailsPanel.Label>Comment:</DetailsPanel.Label>
               <DetailsPanel.Value>{displayComment || '-'}</DetailsPanel.Value>
            </DetailsPanel.Row>
            {hasHistory && (
               <HistoryNavigation>
                  <SharedIconButton
                     $variant="default"
                     $size="sm"
                     onClick={() => setCurrentIndex((i) => i + 1)}
                     disabled={currentIndex >= sortedAuditTrail.length - 1}
                     aria-label="View older entry"
                  >
                     <SSRIcon paths={chevronLeftIcon} />
                  </SharedIconButton>
                  <Caption style={{ minWidth: 60, textAlign: 'center' }}>
                     {currentIndex + 1} of {totalEntries}
                  </Caption>
                  <SharedIconButton
                     $variant="default"
                     $size="sm"
                     onClick={() => setCurrentIndex((i) => i - 1)}
                     disabled={isAtCurrent}
                     aria-label="View newer entry"
                  >
                     <SSRIcon paths={chevronRightIcon} />
                  </SharedIconButton>
               </HistoryNavigation>
            )}
         </DetailsPanel.SectionContent>
      </DetailsPanel.Section>
   );
});

interface DetailsPanelComponentProps {
   selectedCell: SelectedCellInfo | null;
   metadata: FactorMetadata | undefined;
   efValue: number | null;
   shareValue: number | null;
   onClear: () => void;
}

const DetailsPanelComponent: React.FC<DetailsPanelComponentProps> = memo(({ selectedCell, metadata, efValue, shareValue, onClear }) => {
   if (!selectedCell) {
      return (
         <DetailsPanel.Container>
            <DetailsPanel.EmptyMessage>Click on any cell to view its details and change history</DetailsPanel.EmptyMessage>
         </DetailsPanel.Container>
      );
   }

   return (
      <PositionedDetailsContainer>
         <DetailsPanelCloseWrapper>
            <DetailsPanel.CloseButton onClick={onClear} title="Clear selection">
               <SSRIcon paths={crossIcon} />
            </DetailsPanel.CloseButton>
         </DetailsPanelCloseWrapper>
         <DetailsPanelLabel>
            {FEEDSTOCK_LABELS[selectedCell.feedstock]} · {selectedCell.year}
         </DetailsPanelLabel>
         <DetailsPanel.Content>
            <DetailsPanel.Grid>
               <HistorySection
                  title="Emission Factor"
                  value={efValue}
                  auditTrail={metadata?.auditTrails?.emission ?? []}
                  latestBy={metadata?.efUpdatedBy}
                  latestAt={metadata?.efUpdatedAt}
                  latestComment={metadata?.efComment}
               />
               <HistorySection
                  title="Share (%)"
                  value={shareValue}
                  auditTrail={metadata?.auditTrails?.share ?? []}
                  latestBy={metadata?.shareUpdatedBy}
                  latestAt={metadata?.shareUpdatedAt}
                  latestComment={metadata?.shareComment}
                  asPercent
               />
            </DetailsPanel.Grid>
         </DetailsPanel.Content>
      </PositionedDetailsContainer>
   );
});

interface EmissionFactorModalProps {
   visible: boolean;
   onClose: () => void;
   materialGradeId?: string;
   canEditEmissionFactors?: boolean;
}

export const EmissionFactorModal: React.FC<EmissionFactorModalProps> = ({
   visible,
   onClose,
   materialGradeId,
   canEditEmissionFactors = true,
}) => {
   const {
      formState,
      currentRecord,
      loadingInitialGrade,
      isSaving,
      error,
      clearError,
      categories,
      materialTypes,
      subtypes,
      grades,
      loadingCategories,
      loadingMaterialTypes,
      loadingSubtypes,
      loadingGrades,
      loadingRecord,
      loadingDirectInput,
      setSelectedCategory,
      setSelectedMaterialType,
      setSelectedSubtype,
      setSelectedGrade,
      handleDirectCategoryInput,
      handleDirectMaterialTypeInput,
      handleDirectSubtypeInput,
      handleDirectGradeInput,
      updateYearlyValue,
      saveChanges,
      resetChanges,
      clearSelection,
      originalRecord,
   } = useEmissionFactorModal(materialGradeId);


   const [activeTab, setActiveTab] = useState<ModalTab>('data');
   const [visibleFeedstocks, setVisibleFeedstocks] = useState<Set<FeedstockKey>>(
      () => new Set([FEEDSTOCKS[0].key])
   );

   const handleToggleFeedstock = useCallback((key: FeedstockKey | 'all') => {
      if (key === 'all') {
         setVisibleFeedstocks(new Set(FEEDSTOCKS.map((f) => f.key)));
      } else {
         setVisibleFeedstocks(new Set([key]));
      }
   }, []);

   const [selectedCell, setSelectedCell] = useState<SelectedCellInfo | null>(null);
   const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
   const [showReviewModal, setShowReviewModal] = useState(false);

   useEffect(() => {
      setActiveTab('data');
      setSelectedCell(null);
   }, [formState.selectedGrade]);

   const changes = useMemo(
      () => computeChanges(originalRecord, currentRecord),
      [originalRecord, currentRecord]
   );

   const shareErrorsByYear = useMemo(() => {
      if (!currentRecord) return new Map<number, Record<FeedstockKey, string | null>>();
      return new Map(
         currentRecord.yearlyValues.map((yv) => [
            yv.year,
            validateYearlyShares({
               renewable: yv.renewable.share,
               recycled: yv.recycled.share,
               nonRenewable: yv.nonRenewable.share,
            }),
         ])
      );
   }, [currentRecord]);

   const hasValidationErrors = useMemo(
      () => Array.from(shareErrorsByYear.values()).some((errs) => Object.values(errs).some((e) => e !== null)),
      [shareErrorsByYear]
   );

   const validationMessage = useMemo(() => {
      if (!hasValidationErrors) return null;
      const yearMessages: string[] = [];
      shareErrorsByYear.forEach((errors, year) => {
         const firstError = Object.values(errors).find((e) => e !== null);
         if (firstError) yearMessages.push(`${year}: ${firstError}`);
      });
      if (yearMessages.length === 0) return null;
      return `Share validation errors — ${yearMessages.join(' · ')}`;
   }, [hasValidationErrors, shareErrorsByYear]);

   const categoryOptions = useMemo(() => toComboboxOptions(categories), [categories]);
   const materialTypeOptions = useMemo(() => toComboboxOptions(materialTypes), [materialTypes]);
   const subtypeOptions = useMemo(() => toComboboxOptions(subtypes), [subtypes]);
   const gradeOptions = useMemo(() => toComboboxOptions(grades), [grades]);

   const yearlyValuesByYear = useMemo(() => {
      if (!currentRecord) return new Map<number, YearlyEmissionValues>();
      return new Map(currentRecord.yearlyValues.map((yv) => [yv.year, yv]));
   }, [currentRecord]);

   const selectedCellData = useMemo(() => {
      if (!selectedCell) return null;
      return yearlyValuesByYear.get(selectedCell.year)?.[selectedCell.feedstock] ?? null;
   }, [selectedCell, yearlyValuesByYear]);

   const handleCellSelect = useCallback((cell: SelectedCellInfo) => {
      setSelectedCell(cell);
   }, []);

   const handleClearCell = useCallback(() => {
      setSelectedCell(null);
   }, []);

   const handleClose = useCallback(() => {
      if (formState.hasChanges && !showUnsavedWarning) {
         setShowUnsavedWarning(true);
         return;
      }
      onClose();
   }, [formState.hasChanges, showUnsavedWarning, onClose]);

   const handleGoBack = useCallback(() => setShowUnsavedWarning(false), []);

   useEscapeKey(handleClose, visible && !showReviewModal);

   const handleSaveClick = useCallback(() => setShowReviewModal(true), []);

   const handleReviewConfirm = useCallback(
      (comments: Record<string, string>) => {
         saveChanges(comments);
         setShowReviewModal(false);
      },
      [saveChanges]
   );

   const handleReviewCancel = useCallback(() => setShowReviewModal(false), []);

   if (!visible) return null;

   const hasNoData = !loadingInitialGrade && !loadingCategories && categories.length === 0;
   const isLoadingAny = loadingCategories || loadingMaterialTypes || loadingSubtypes || loadingGrades || loadingDirectInput;

   return ReactDOM.createPortal(
      <Modal.Overlay>
         <WideModalContainer
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
         >
            <Modal.Header>
               <Modal.Title>
                  <ModalTitleRow>
                     Emission Factors
                     {formState.hasChanges && <Badge variant="warning">Unsaved</Badge>}
                     {loadingDirectInput && <LoadingBall size="small" color="primary" />}
                  </ModalTitleRow>
               </Modal.Title>
               <IconButton onClick={handleClose} icon={crossIcon} title="Close" />
            </Modal.Header>

            <WideModalContent>
               {(loadingInitialGrade || loadingCategories) && !categories.length && (
                  <LoadingState>
                     <Loading text="Loading categories..." labelPosition="center">
                        <LoadingBall size="medium" color="primary" />
                     </Loading>
                  </LoadingState>
               )}

               {error && (
                  <Alert variant="error">
                     <DismissibleAlertRow>
                        <span>{error}</span>
                        <SharedIconButton $variant="ghost" $size="sm" onClick={clearError} title="Dismiss">
                           <SSRIcon paths={crossIcon} />
                        </SharedIconButton>
                     </DismissibleAlertRow>
                  </Alert>
               )}

               {hasNoData && <Alert variant="warning">No emission factor data available</Alert>}

               {!hasNoData && categories.length > 0 && (
                  <>
                     <DropdownGrid>
                        <ComboboxDropdown
                           label="Material category ID"
                           value={formState.selectedCategory}
                           options={categoryOptions}
                           onChange={setSelectedCategory}
                           onDirectInput={handleDirectCategoryInput}
                           loading={loadingCategories}
                           placeholder="Select or type ID..."
                        />
                        <ComboboxDropdown
                           label="Material Type ID"
                           value={formState.selectedMaterialType}
                           options={materialTypeOptions}
                           onChange={setSelectedMaterialType}
                           onDirectInput={handleDirectMaterialTypeInput}
                           loading={loadingMaterialTypes}
                           placeholder="Select or type ID..."
                        />
                        <ComboboxDropdown
                           label="Material subtype ID"
                           value={formState.selectedSubtype}
                           options={subtypeOptions}
                           onChange={setSelectedSubtype}
                           onDirectInput={handleDirectSubtypeInput}
                           loading={loadingSubtypes}
                           placeholder="Select or type ID..."
                        />
                        <DropdownWithClear>
                           <ComboboxDropdown
                              label="Material grade ID"
                              value={formState.selectedGrade}
                              options={gradeOptions}
                              onChange={setSelectedGrade}
                              onDirectInput={handleDirectGradeInput}
                              loading={loadingGrades}
                              placeholder="Select or type ID..."
                           />
                           {(formState.selectedCategory || formState.selectedMaterialType || formState.selectedSubtype || formState.selectedGrade) && (
                              <ClearButton type="button" onClick={clearSelection}>
                                 Clear
                              </ClearButton>
                           )}
                        </DropdownWithClear>
                     </DropdownGrid>

                     {loadingRecord && (
                        <LoadingState>
                           <Loading text="Loading emission factor data..." labelPosition="center">
                              <LoadingBall size="medium" color="primary" />
                           </Loading>
                        </LoadingState>
                     )}

                     {formState.selectedGrade && currentRecord && !loadingRecord && (
                        <>
                           <TabContainer>
                              <Tab $active={activeTab === 'data'} onClick={() => setActiveTab('data')}>
                                 Data
                              </Tab>
                              <Tab $active={activeTab === 'charts'} onClick={() => setActiveTab('charts')}>
                                 Charts
                              </Tab>
                           </TabContainer>

                           {activeTab === 'data' && (
                              <>
                                 <DataTable.Card>
                                    <DataTable.Table>
                                       <DataTable.Head>
                                          <DataTable.HeaderRow>
                                             <DataTable.HeaderCell style={{ width: '80px' }}>Feedstock</DataTable.HeaderCell>
                                             <DataTable.HeaderCell style={{ width: '50px' }} />
                                             {currentRecord.availableYears.map((year) => (
                                                <DataTable.HeaderCell key={year} $align="center" style={{ minWidth: '80px' }}>
                                                   {year}
                                                </DataTable.HeaderCell>
                                             ))}
                                          </DataTable.HeaderRow>
                                       </DataTable.Head>
                                       <DataTable.Body>
                                          {FEEDSTOCKS.map(({ key, label }) => (
                                             <React.Fragment key={key}>
                                                {(['emission', 'share'] as const).map((field, fieldIndex) => (
                                                   <DataTable.Row key={`${key}-${field}`} $groupEnd={fieldIndex === 1}>
                                                      {fieldIndex === 0 && <DataTable.LabelCell rowSpan={2}>{label}</DataTable.LabelCell>}
                                                      <DataTable.SubLabelCell>{field === 'emission' ? 'EF' : 'Share (%)'}</DataTable.SubLabelCell>
                                                      {currentRecord.availableYears.map((year) => {
                                                         const yearData = yearlyValuesByYear.get(year);
                                                         return (
                                                            <ValueCell
                                                               key={year}
                                                               value={yearData?.[key]?.[field] ?? null}
                                                               year={year}
                                                               feedstockKey={key}
                                                               field={field}
                                                               isSelected={selectedCell?.year === year && selectedCell?.feedstock === key}
                                                               disabled={!canEditEmissionFactors}
                                                               validationError={field === 'share' ? shareErrorsByYear.get(year)?.[key] : null}
                                                               onChange={updateYearlyValue}
                                                               onSelect={handleCellSelect}
                                                            />
                                                         );
                                                      })}
                                                   </DataTable.Row>
                                                ))}
                                             </React.Fragment>
                                          ))}
                                       </DataTable.Body>
                                    </DataTable.Table>
                                 </DataTable.Card>

                                 {currentRecord.availableYears.length === 0 && (
                                    <EmptyState>
                                       <EmptyStateText>No years defined for this grade.</EmptyStateText>
                                    </EmptyState>
                                 )}

                                 <DetailsPanelComponent
                                    selectedCell={selectedCell}
                                    metadata={selectedCellData?.metadata}
                                    efValue={selectedCellData?.emission ?? null}
                                    shareValue={selectedCellData?.share ?? null}
                                    onClear={handleClearCell}
                                 />
                              </>
                           )}

                           {activeTab === 'charts' && (
                              <>
                                 <ChartControls>
                                    <ChartControlGroup>
                                       <FeedstockToggle
                                          $active={visibleFeedstocks.size === FEEDSTOCKS.length}
                                          $color={theme.colors.border.focus}
                                          onClick={() => handleToggleFeedstock('all')}
                                          aria-pressed={visibleFeedstocks.size === FEEDSTOCKS.length}
                                       >
                                          All Feedstocks
                                       </FeedstockToggle>
                                       {FEEDSTOCKS.map(({ key, label }) => (
                                          <FeedstockToggle
                                             key={key}
                                             $active={visibleFeedstocks.has(key)}
                                             $color={FEEDSTOCK_COLORS[key]}
                                             onClick={() => handleToggleFeedstock(key)}
                                             aria-pressed={visibleFeedstocks.has(key)}
                                          >
                                             {label}
                                          </FeedstockToggle>
                                       ))}
                                    </ChartControlGroup>
                                 </ChartControls>
                                 <EmissionFactorCharts
                                    yearlyValues={currentRecord.yearlyValues}
                                    visibleFeedstocks={visibleFeedstocks}
                                    visibleMetrics={VISIBLE_METRICS}
                                 />
                              </>
                           )}
                        </>
                     )}

                     {!formState.selectedGrade && !loadingRecord && !isLoadingAny && (
                        <EmptyState>
                           <EmptyStateText>Select a material grade to view and edit emission factors</EmptyStateText>
                        </EmptyState>
                     )}
                     {formState.selectedGrade && !currentRecord && !loadingRecord && (
                        <EmptyState>
                           <EmptyStateText>Unable to load data for this grade. Try refreshing.</EmptyStateText>
                        </EmptyState>
                     )}
                  </>
               )}

               {showUnsavedWarning && (
                  <Alert variant="error">
                     <SSRIcon paths={warningTriangleIcon} />
                     <span>
                        <strong>Unsaved Changes:</strong> You have unsaved changes that will be lost.
                        Click "Discard" to close without saving, or "Go Back" to continue editing.
                     </span>
                  </Alert>
               )}
            </WideModalContent>

            <Modal.Footer>
               {showUnsavedWarning ? (
                  <>
                     <Button text="Go Back" type="secondary" onClick={handleGoBack} small />
                     <FooterSpacer />
                     <Button text="Discard" type="emphasised" onClick={onClose} small />
                  </>
               ) : (
                  <>
                     <Button text="Close" type="secondary" onClick={handleClose} small />
                     <FooterSpacer />
                     {validationMessage && (
                        <FooterValidationMessage>
                           <SSRIcon paths={warningTriangleIcon} />
                           {validationMessage}
                        </FooterValidationMessage>
                     )}
                     <Button text="Reset" type="secondary" onClick={resetChanges} disabled={changes.length === 0 || isSaving} small />
                     <Button
                        text={isSaving ? 'Saving...' : 'Save'}
                        type="primary"
                        onClick={handleSaveClick}
                        disabled={changes.length === 0 || isSaving || hasValidationErrors}
                        small
                     />
                  </>
               )}
            </Modal.Footer>

            <ReviewChangesModal
               visible={showReviewModal}
               changes={changes}
               onConfirm={handleReviewConfirm}
               onCancel={handleReviewCancel}
               isSaving={isSaving}
            />
         </WideModalContainer>
      </Modal.Overlay>,
      document.body
   );
};

export default EmissionFactorModal;